/** @jsxRuntime classic */
/** @jsx jsx */
import React, {Fragment, useContext, useEffect, useState} from 'react';
import {css, jsx} from '@emotion/react';
import {format as formatDate, parse as parseDate} from 'date-fns';
import {cloneDeep, get} from 'lodash';
import Select from 'react-select';
import axios from 'axios';
import LpnService, {LpnCorrectionRequest, LpnMoveRequest} from '../../../shared/services/LpnService';
import {renderItemLink, renderLpnLink} from '../../../../libs/helpers';
import EditLpnMenu from '../../../shared/EditLpnMenu';
import UpdateControls from '../../../dropoffs/shared/UpdateControls';
import CustomParams from '../../../dropoffs/lpns/CustomParams';
import {Inbound, InboundLpn, InboundLpnItem} from '../InboundInterfaces';
import FlexeContext from '../../../contexts/FlexeContext';
import {Inventory, Packaging} from '../../../shared/CommonInterfaces';
import TypeAhead, {TypeAheadOption} from '../../../shared/TypeAhead';
import {InboundApiResponse} from '../../warehouse/InboundDeliveryDetails';

interface LpnContentRowProps {
  lpn: InboundLpn;
  item: InboundLpnItem;
  inbound: Inbound;
  reservationId: number;
  shouldDisplayLpn: boolean;
  shouldDisplayUoms: boolean;
  isAsnLpns: boolean;
  enableEdits: boolean;
  isShipper: boolean;
  lpnReceiveOnly: boolean; // To show damaged column
  inProgressContainerIds: number[];
  onActionComplete: () => void;
}

// eslint-disable-next-line
const checkmarkImg = require('../../../../../assets/images/common/checkmark-green.png');

export const LpnContentRow: React.FC<LpnContentRowProps> = (props) => {
  const {
    lpn,
    item,
    inbound,
    reservationId,
    shouldDisplayLpn,
    shouldDisplayUoms,
    isAsnLpns,
    enableEdits,
    isShipper,
    inProgressContainerIds,
    onActionComplete
  } = props;

  const {authenticityToken} = useContext(FlexeContext);
  const lpnService = new LpnService(authenticityToken);

  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [editedItem, setEditedItem] = useState<InboundLpnItem>(cloneDeep(item));
  const [skuQuery, setSkuQuery] = useState(item.sku);
  const [inventoryOptions, setInventoryOptions] = useState<Inventory[]>([]);
  const [typeAheadOptions, setTypeAheadOptions] = useState<TypeAheadOption[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMoveToList, setShowMoveToList] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [saveButtonEnabled, setSaveButtonEnabled] = useState(true);

  // Update local state when item prop changes (e.g., after refresh)
  useEffect(() => {
    setEditedItem(cloneDeep(item));
    setSkuQuery(item.sku);
  }, [item]);

  useEffect(() => {
    const closeDropdownHandler = (event: MouseEvent) => {
      let element = event.target as Element;
      while (element.parentElement) {
        if (element.classList?.contains('dropdown-menu')) return;
        element = element.parentElement;
      }
      setShowDropdown(false);
    };
    document.body.addEventListener('mouseup', closeDropdownHandler);
    return () => document.body.removeEventListener('mouseup', closeDropdownHandler);
  }, []);

  const handleApiError = (errorMessages: string[], response?: InboundApiResponse) => {
    const newErrors = [...errorMessages];
    if (response?.errors) {
      newErrors[0] += ':';
      response.errors.forEach((e) => newErrors.push(e.detail));
    }
    setErrors(newErrors);
  };

  const handleSkuQuery = async (query: string) => {
    setSkuQuery(query);
    if (query.length > 2) {
      try {
        const response = await axios.get('/widgets/inventory_picker', {
          params: {q: query, reservation_id: reservationId}
        });
        const inventories: Inventory[] = get(response, 'data.matches', []);
        setInventoryOptions(inventories);
        setTypeAheadOptions(
          inventories.map((inv) => ({
            value: inv.sku,
            displayName: `${inv.sku} - ${inv.description}`
          }))
        );
      } catch (error) {
        handleApiError(['There was an error getting SKU information.']);
      }
    }
  };

  const handleSkuSelect = (sku: string) => {
    const selectedInventory = inventoryOptions.find((inv) => inv.sku === sku);
    if (selectedInventory) {
      const newItem = cloneDeep(editedItem);
      newItem.id = selectedInventory.id;
      newItem.sku = selectedInventory.sku;
      newItem.description = selectedInventory.description;
      setEditedItem(newItem);
      setSkuQuery(sku);
      setTypeAheadOptions([]);
    }
  };

  const handleFieldUpdate = (field: string, value: any) => {
    const newItem = cloneDeep(editedItem);
    switch (field) {
      case 'receivedQuantity':
        newItem.receivedQuantities.amount = Number(value);
        break;
      case 'packaging':
        newItem.receivedQuantities.packaging = value;
        break;
      case 'lotCode':
        newItem.itd.lotCode = value;
        break;
      case 'expirationDate':
        newItem.itd.expirationDate = value;
        break;
    }
    setEditedItem(newItem);
  };

  const startEditing = () => setIsEditing(true);
  const stopEditing = () => {
    setIsEditing(false);
    setEditedItem(cloneDeep(item)); // Reset changes
    setErrors([]);
  };

  const updateLpn = async () => {
    setSaveButtonEnabled(false);
    const request: LpnCorrectionRequest = {
      reservationId,
      lpn: lpn.barcode,
      currentSku: item.sku
    };

    if (item.sku !== editedItem.sku) request.targetSku = editedItem.sku;
    if (item.itd.lotCode !== editedItem.itd.lotCode) request.targetLotCode = editedItem.itd.lotCode;
    if (item.itd.expirationDate !== editedItem.itd.expirationDate) {
      request.targetExpirationDate = editedItem.itd.expirationDate ? parseDate(editedItem.itd.expirationDate) : null;
    }
    if (item.receivedQuantities.amount !== editedItem.receivedQuantities.amount) {
      request.targetQuantity = editedItem.receivedQuantities.amount;
    }
    if (item.receivedQuantities.packaging !== editedItem.receivedQuantities.packaging) {
      request.targetUom = editedItem.receivedQuantities.packaging as Packaging;
    }

    const response = await lpnService.correctLpnInbound(request);
    if (response?.errors?.length === 0) {
      stopEditing();
      onActionComplete();
    } else {
      handleApiError(['There was an error updating the LPN data'], response);
    }
    setSaveButtonEnabled(true);
  };

  const deleteLPN = async () => {
    setShowDeleteConfirmation(false);
    const request: LpnCorrectionRequest = {
      reservationId,
      lpn: lpn.barcode,
      currentSku: item.sku,
      targetQuantity: 0
    };
    const response = await lpnService.correctLpnInbound(request);
    if (response?.errors?.length === 0) {
      onActionComplete();
    } else {
      handleApiError(['There was an error deleting the LPN'], response);
    }
  };

  const moveLPNto = async (moveToInboundId: number) => {
    setShowDropdown(false);
    setShowMoveToList(false);
    const request: LpnMoveRequest = {
      reservationId,
      lpn: lpn.barcode,
      sourceContainerDeliveryId: inbound.id,
      targetContainerDeliveryId: moveToInboundId
    };
    const response = await lpnService.moveLpnToDropoff(request);
    if (response?.errors === undefined) {
      onActionComplete();
    } else {
      handleApiError(['There was an error moving the LPN'], response);
    }
  };

  const renderSkuColumn = () => {
    return isEditing ? (
      <TypeAhead
        name="sku-select"
        placeholder="Type to select SKU..."
        value={skuQuery}
        options={typeAheadOptions}
        onRequestOptions={handleSkuQuery}
        onSelect={handleSkuSelect}
      />
    ) : (
      <div>{renderItemLink(item.id, item.sku, isShipper)}</div>
    );
  };

  const renderReceivedQuantityColumn = () => {
    const uomOptions = [
      {value: 'each', label: 'each'},
      {value: 'carton', label: 'carton'}
    ];
    return isEditing ? (
      <Fragment>
        <div className="form-inline quantity-edit">
          <input
            type="number"
            className="form-control"
            value={editedItem.receivedQuantities.amount}
            min="1"
            step="1"
            onChange={(e) => handleFieldUpdate('receivedQuantity', e.target.value)}
          />
        </div>
        <div className="form-inline uom-edit">
          <Select
            options={uomOptions}
            defaultValue={{
              value: editedItem.receivedQuantities.packaging,
              label: editedItem.receivedQuantities.packaging
            }}
            onChange={(opt) => handleFieldUpdate('packaging', opt.value)}
          />
        </div>
      </Fragment>
    ) : item.receivedQuantities ? (
      `${item.receivedQuantities.amount} ${item.receivedQuantities.packaging}`
    ) : (
      '-'
    );
  };

  return (
    <div data-testid="inbound-lpn-content-row" className="lpn-row">
      {showDeleteConfirmation && (
        <div className="delete-modal">
          <div className="modal-backdrop"></div>
          <div className="modal" role="dialog">
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{`Delete LPN ${lpn.barcode}`}</h5>
                </div>
                <div className="modal-body">
                  Are you sure you want to delete this LPN? This action cannot be undone.
                </div>
                <div className="modal-footer">
                  <button type="button" onClick={deleteLPN} className="btn">
                    Yes, Permanently delete this LPN
                  </button>
                  <button type="button" onClick={() => setShowDeleteConfirmation(false)} className="btn btn-default">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {errors.length > 0 && <div className="alert alert-danger">{errors.join(' ')}</div>}
      <div className="row">
        <div className="col-md-1">
          {shouldDisplayLpn && isAsnLpns && item.receivedQuantities != null && (
            <img
              data-testid="lpn-received-checkmark"
              src={checkmarkImg}
              alt="Checkmark"
              css={css`
                padding: 5px;
              `}
            />
          )}
          {shouldDisplayLpn && renderLpnLink(lpn.barcode, isShipper, lpn.id)}
        </div>
        <div className="col-md-2">{renderSkuColumn()}</div>
        <div className="col-md-2">{lpn.purchaseOrder || '-'}</div>
        {shouldDisplayUoms && item.uoms && (
          <div className="col-md-1">
            {item.uoms?.eachesPerInnerPack || '-'} each/inner pack
            <br />
            {item.uoms?.eachesPerOuterCase || '-'} each/outer case
          </div>
        )}
        {isAsnLpns && (
          <div className="col-md-1" data-testid="lpn-expected-quantity">
            {item.expectedQuantity?.amount} {item.expectedQuantity?.packaging}
          </div>
        )}
        <div className="col-md-1">{renderReceivedQuantityColumn()}</div>
        <div className="col-md-1">{lpn.receivedAt ? formatDate(lpn.receivedAt, 'MM/DD/YYYY') : '-'}</div>
        <div className="col-md-2">
          <CustomParams
            handleLotChange={(e) => handleFieldUpdate('lotCode', e.target.value)}
            handleDateChange={(date) => handleFieldUpdate('expirationDate', date)}
            lotCode={editedItem.itd.lotCode}
            expirationDate={editedItem.itd.expirationDate ? parseDate(editedItem.itd.expirationDate) : null}
            editing={isEditing}
            serialNumbers={null} // TODO will be implemented in different ticket
          />
        </div>
        <div className="col-md-1">{lpn.isDamaged ? 'Yes' : 'No'}</div>
        {enableEdits && !isEditing && (
          <EditLpnMenu
            showEditToggle={true}
            showDropdown={showDropdown}
            showMoveToList={showMoveToList}
            containerDeliveryId={inbound.id}
            inProgressDropoffIds={inProgressContainerIds || []}
            toggleShowDropdown={() => setShowDropdown(!showDropdown)}
            toggleShowMoveToList={() => setShowMoveToList(!showMoveToList)}
            editLpn={startEditing}
            moveLPNto={moveLPNto}
            confirmLPNDelete={() => setShowDeleteConfirmation(true)}
          />
        )}
        {isEditing && (
          <UpdateControls saveButtonEnabled={saveButtonEnabled} cancelHandler={stopEditing} saveHandler={updateLpn} />
        )}
      </div>
    </div>
  );
};
